/**
 * Pure, framework-free authentication validation and mapping helpers.
 *
 * These functions back the registration/verification/federated sign-in UI and
 * the AuthProvider. They are intentionally side-effect free (no React, Amplify,
 * DOM, or I/O) so they can be exercised directly by property-based tests.
 *
 * Design refs: Components 3, 6, 7; Properties 1, 2, 4, 5, 6, 7, 9, 12.
 */

/**
 * Per-rule and overall outcome of evaluating a password against the Cognito
 * User Pool password policy.
 *
 * The individual flags let the UI render inline, per-rule feedback while the
 * user types (Requirement 1.4); `isValid` gates submission (Requirement 1.5).
 */
export interface PasswordPolicyResult {
  /** Password is at least 8 characters long. */
  minLength: boolean;
  /** Password contains at least one uppercase letter (A-Z). */
  hasUppercase: boolean;
  /** Password contains at least one lowercase letter (a-z). */
  hasLowercase: boolean;
  /** Password contains at least one digit (0-9). */
  hasDigit: boolean;
  /** Password contains at least one symbol (any non-alphanumeric character). */
  hasSymbol: boolean;
  /** True iff every individual rule above is satisfied. */
  isValid: boolean;
}

/**
 * Evaluate a password against the User Pool password policy.
 *
 * The policy requires a minimum length of 8 characters and at least one
 * uppercase letter, one lowercase letter, one digit, and one symbol. A "symbol"
 * is any character that is not an ASCII letter or digit ([^A-Za-z0-9]), which
 * mirrors Cognito's `requireSymbols` behaviour.
 *
 * Pure function: no side effects. Robust to the empty string and very long
 * strings.
 *
 * Validates: Requirements 1.4, 1.5 (Property 1).
 *
 * @param password The candidate password to evaluate.
 * @returns Per-rule flags plus an overall `isValid` boolean.
 */
export function validatePasswordPolicy(password: string): PasswordPolicyResult {
  const minLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);

  const isValid =
    minLength && hasUppercase && hasLowercase && hasDigit && hasSymbol;

  return {
    minLength,
    hasUppercase,
    hasLowercase,
    hasDigit,
    hasSymbol,
    isValid,
  };
}
/**
 * Validate the syntax of an email address well enough to gate registration
 * submission before any account-creation (`signUp`) request is made.
 *
 * This is a deliberately pragmatic, single-`@` check rather than an
 * RFC-5322-exhaustive parser: it requires a non-empty local part, exactly one
 * `@`, a domain that contains at least one dot with non-empty labels on either
 * side, and no whitespace anywhere. This rejects the obvious invalids (missing
 * `@`, empty local/domain parts, embedded spaces) while accepting the ordinary
 * addresses users actually type.
 *
 * Pure function: no side effects. Robust to the empty string.
 *
 * Validates: Requirements 1.6 (Property 2).
 *
 * @param email The candidate email address to validate.
 * @returns True iff the address is syntactically acceptable for submission.
 */
export function isValidEmail(email: string): boolean {
  if (typeof email !== 'string' || email.length === 0) {
    return false;
  }
  // No whitespace anywhere.
  if (/\s/.test(email)) {
    return false;
  }
  // Exactly one '@' separating a non-empty local part from the domain.
  const atIndex = email.indexOf('@');
  if (atIndex <= 0 || atIndex !== email.lastIndexOf('@')) {
    return false;
  }
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);
  if (local.length === 0 || domain.length === 0) {
    return false;
  }
  // Domain must have at least one dot with non-empty labels on both sides
  // (i.e. no leading/trailing dot and no empty label such as `a..b`).
  const labels = domain.split('.');
  if (labels.length < 2) {
    return false;
  }
  return labels.every((label) => label.length > 0);
}

/**
 * Validate that a confirmation code has the exact expected format of six
 * numeric digits.
 *
 * Used to reject malformed codes on the verification screen before any
 * `confirmSignUp` request is made, while the entered input is retained
 * (Requirement 2.5).
 *
 * Pure function: no side effects. Robust to the empty string.
 *
 * Validates: Requirements 2.5 (Property 4).
 *
 * @param code The candidate confirmation code.
 * @returns True iff `code` matches exactly `^[0-9]{6}$`.
 */
export function isValidConfirmationCodeFormat(code: string): boolean {
  return typeof code === 'string' && /^[0-9]{6}$/.test(code);
}

/**
 * Partially mask an email address for display on the verification/resend
 * screen, revealing at most the first character of the local part and
 * preserving the domain.
 *
 * The output never contains the full local part as a substring: for a valid
 * `local@domain` it reveals at most the first character of `local` and masks
 * the remainder with asterisks (e.g. `j****@example.com`). A single-character
 * local part is still masked (e.g. `j*@domain`) so that the masked value never
 * equals the original local part. If the input has no `@` (a defensive case
 * that should not occur for a validated address), a fully-masked placeholder is
 * returned instead of throwing.
 *
 * Pure function: no side effects.
 *
 * Validates: Requirements 3.3 (Property 5).
 *
 * @param email The email address to mask (expected to be syntactically valid).
 * @returns A masked representation safe to display to the User.
 */
export function maskEmail(email: string): string {
  const atIndex = typeof email === 'string' ? email.indexOf('@') : -1;
  if (atIndex <= 0) {
    // Defensive: no usable local part / no '@'. Return a fully-masked
    // placeholder without revealing anything and without throwing.
    return '****';
  }

  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex); // includes the leading '@'

  // Reveal at most the first character; always append at least one mask so the
  // result can never equal the original local part (even for a 1-char local).
  const firstChar = local.charAt(0);
  const maskedLocal = firstChar + '*'.repeat(Math.max(1, local.length - 1));

  return `${maskedLocal}${domain}`;
}

/**
 * The single, fixed user-facing message shown for every enumeration-sensitive
 * sign-in failure. Using one identical string ensures a valid account cannot be
 * distinguished from an invalid, unknown, or unverified one.
 *
 * Validates: Requirements 4.2, 4.4 (Property 6).
 */
export const GENERIC_SIGNIN_ERROR = 'Email or password is incorrect.';

/**
 * Map a Cognito/Amplify sign-in error name to a user-facing message.
 *
 * The enumeration-sensitive failure variants — `NotAuthorizedException`
 * (invalid credentials), `UserNotFoundException` (unknown account), and
 * `UserNotConfirmedException` (unverified account) — all collapse to the same
 * {@link GENERIC_SIGNIN_ERROR} string so that a valid account cannot be
 * distinguished from an invalid or unverified one. Any other/unknown error name
 * maps to the same generic fallback, since sign-in never needs to disclose more.
 *
 * Pure function: no side effects.
 *
 * Validates: Requirements 4.2, 4.4 (Property 6).
 *
 * @param errorName The Cognito/Amplify error name (e.g. `NotAuthorizedException`).
 * @returns The user-facing message to display.
 */
export function mapSignInError(errorName: string): string {
  // Every sign-in failure is non-enumerating: a single fixed string regardless
  // of the underlying cause.
  void errorName;
  return GENERIC_SIGNIN_ERROR;
}

/**
 * Decide whether a federated OAuth redirect response is trustworthy enough to
 * establish an authenticated session, based solely on the OAuth `state`
 * parameter.
 *
 * A session may be established **iff** the returned state is present (a
 * non-empty string) **and** strictly equals the state originally sent. A
 * missing (null/undefined/empty) or mismatched returned state yields `false`,
 * which the caller uses to reject the response and remain unauthenticated
 * without establishing a session.
 *
 * Pure function: no side effects.
 *
 * Validates: Requirements 10.2, 10.3 (Property 12).
 *
 * @param sentState The state value the Application originally sent.
 * @param returnedState The state value returned from the Hosted UI redirect.
 * @returns True iff a session should be established.
 */
export function shouldEstablishSession(
  sentState: string | null | undefined,
  returnedState: string | null | undefined,
): boolean {
  if (typeof returnedState !== 'string' || returnedState.length === 0) {
    return false;
  }
  return returnedState === sentState;
}

/**
 * The exact set of OAuth scopes requested by the Application: `openid`,
 * `email`, and `profile`, and nothing more.
 *
 * This is the single source of truth for the requested scopes, reused by both
 * the CDK app-client configuration and the Amplify OAuth configuration so the
 * two can never drift (Property 7).
 *
 * Validates: Requirements 6.5, 10.4 (Property 7).
 */
export const OAUTH_SCOPES = ['openid', 'email', 'profile'] as const;

/**
 * Validate that a redirect URL is acceptable to register as a callback or
 * sign-out URL: it must parse as a URL, and it must either use the `https`
 * scheme or have the host `localhost` (on any port).
 *
 * Any URL that fails to parse, or that uses a non-HTTPS scheme with a host
 * other than `localhost`, is rejected.
 *
 * Pure function: no side effects.
 *
 * Validates: Requirements 6.6 (Property 8).
 *
 * @param url The candidate redirect URL.
 * @returns True iff the URL is HTTPS, or its host is exactly `localhost`.
 */
export function isAllowedRedirectUrl(url: string): boolean {
  if (typeof url !== 'string' || url.length === 0) {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol === 'https:') {
    return true;
  }
  return parsed.hostname === 'localhost';
}

/**
 * Derive the Application's tenant identifier from a set of decoded token
 * claims. The tenant identifier is exactly the Cognito subject identifier
 * (`sub`); when no `sub` is present the derivation yields `null`.
 *
 * Because this is a pure function of `sub`, an account whose `sub` is unchanged
 * resolves to the identical `tenantId` across all sessions and sign-in methods
 * (Requirement 8.2).
 *
 * Pure function: no side effects.
 *
 * Validates: Requirements 8.1, 8.2 (Property 9).
 *
 * @param claims The decoded token claims, or null/undefined when absent.
 * @returns The resolved tenant identifier, or `null` when no `sub` is present.
 */
export function deriveTenantId(
  claims: { sub?: string } | null | undefined,
): string | null {
  return claims?.sub ?? null;
}
